import React from "react";
import { csrfFetch } from "../lib/installCsrfAxios";
import { isPilotPage, reportPilotIssue } from "../lib/pilotDiagnostics";

/**
 * Top-level React error boundary (launch checklist lp1).
 *
 * Before this, a render-time crash anywhere in the tree blanked the screen with
 * no signal to the user or the team. This catches the crash, shows a recoverable
 * fallback, and reports it to the backend at POST /api/internal/error-report
 * (same-origin; the endpoint is rate-limited and scrubs nothing sensitive here).
 *
 * Self-contained on purpose: inline styles + raw fetch, no app modules — so it
 * still renders if the thing that crashed is shared UI/CSS/an API client.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
    this._reported = false;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) || "Unexpected error" };
  }

  componentDidCatch(error, info) {
    // Console for local dev visibility.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] caught", error, info);
    if (this._reported) return; // one report per mounted error
    this._reported = true;
    if (isPilotPage()) {
      void reportPilotIssue("frontend_exception").catch(() => {});
      return;
    }
    try {
      const body = JSON.stringify({
        message: (error && error.message) || "Unknown error",
        stack: error && error.stack ? String(error.stack) : undefined,
        componentStack: info && info.componentStack ? String(info.componentStack) : undefined,
        url: typeof window !== "undefined" ? window.location.href : undefined,
        meta: { boundary: this.props.name || "root" },
      });
      // keepalive so the report survives a quick navigation/reload.
      csrfFetch("/api/internal/error-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        credentials: "include",
        keepalive: true,
      }).catch(() => {
        /* reporting is best-effort; never throw from the boundary */
      });
    } catch {
      /* never throw from the boundary */
    }
  }

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  handleHome = () => {
    if (typeof window !== "undefined") window.location.assign("/");
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: "60vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          textAlign: "center",
          fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          color: "#1e293b",
        }}
      >
        <div style={{ maxWidth: 480 }}>
          <h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>Something went wrong</h1>
          <p style={{ color: "#64748b", marginBottom: "1.5rem" }}>
            This part of the app hit an unexpected error. The team has been notified. You can
            reload, or head back to the homepage.
          </p>
          <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
            <button
              onClick={this.handleReload}
              style={{
                background: "#0f172a",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "0.6rem 1.1rem",
                fontSize: "0.95rem",
                cursor: "pointer",
              }}
            >
              Reload
            </button>
            <button
              onClick={this.handleHome}
              style={{
                background: "transparent",
                color: "#0f172a",
                border: "1px solid #cbd5e1",
                borderRadius: 8,
                padding: "0.6rem 1.1rem",
                fontSize: "0.95rem",
                cursor: "pointer",
              }}
            >
              Go to homepage
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
